import { daysBetween } from "@shared/types";

const WD_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const MONTHS_GEN = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];
const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

const parts = (d: string) => d.split("-").map(Number) as [number, number, number];

export function weekday(d: string) {
  const [y, m, day] = parts(d);
  return WD_SHORT[new Date(Date.UTC(y, m - 1, day)).getUTCDay()];
}
export function dayNum(d: string) {
  return parts(d)[2];
}
export function fmtDay(d: string) {
  const [, m, day] = parts(d);
  return `${day} ${MONTHS_GEN[m - 1]}`;
}
export function fmtDayShort(d: string) {
  const [, m, day] = parts(d);
  return `${day} ${MONTHS_SHORT[m - 1]}`;
}
export function fmtRange(a: string, b: string) {
  const [ya, ma, da] = parts(a);
  const [yb, mb, db] = parts(b);
  if (ya === yb && ma === mb) return `${da}–${db} ${MONTHS_GEN[ma - 1]} ${ya}`;
  return `${da} ${MONTHS_GEN[ma - 1]} – ${db} ${MONTHS_GEN[mb - 1]} ${yb}`;
}

export function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function countdown(start: string, end: string) {
  const today = todayLocal();
  const toStart = daysBetween(today, start);
  if (toStart > 0) return { kind: "before" as const, days: toStart, text: `через ${toStart} ${plural(toStart, "день", "дня", "дней")}` };
  const toEnd = daysBetween(today, end);
  if (toEnd >= 0) return { kind: "during" as const, days: -toStart + 1, text: `день ${-toStart + 1}` };
  return { kind: "after" as const, days: -toEnd, text: "поездка завершена" };
}

export function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** ISO → «18:00» в часовом поясе tz */
export function timeIn(iso: string | null, tz: string | null | undefined) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit", timeZone: tz ?? undefined }).format(new Date(iso));
  } catch {
    return null;
  }
}
/** ISO → «7 апр, 13:30» в поясе tz */
export function dateTimeIn(iso: string | null, tz: string | null | undefined) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: tz ?? undefined }).format(new Date(iso));
  } catch {
    return null;
  }
}
/** Дата (YYYY-MM-DD) ISO-момента в поясе tz */
export function localDateIn(iso: string, tz: string | null | undefined) {
  try {
    return new Intl.DateTimeFormat("sv-SE", { timeZone: tz ?? undefined }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

export function durationText(min: number | null | undefined) {
  if (!min) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `${m} мин`;
  return m ? `${h} ч ${m} мин` : `${h} ч`;
}

export function money(n: number, currency = "EUR") {
  const digits = currency === "VND" ? 0 : n >= 100 ? 0 : 2;
  try {
    return new Intl.NumberFormat("ru-RU", { style: "currency", currency, maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n);
  } catch {
    return `${n.toFixed(digits)} ${currency}`;
  }
}

export function relTime(iso: string) {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "только что";
  if (s < 3600) return `${Math.floor(s / 60)} мин назад`;
  if (s < 86400) return `${Math.floor(s / 3600)} ч назад`;
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" }).format(new Date(iso));
}

export const fmtSec = (s: number) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`;
};

/** datetime-local (YYYY-MM-DDTHH:mm в поясе tz) ↔ ISO со смещением */
export function isoToLocalInput(iso: string | null, tz: string | null | undefined) {
  if (!iso) return "";
  try {
    const f = new Intl.DateTimeFormat("sv-SE", { timeZone: tz ?? undefined, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
    return f.replace(" ", "T");
  } catch {
    return iso.slice(0, 16);
  }
}
export function localInputToIso(v: string, tz: string | null | undefined) {
  if (!v) return null;
  // местное «настенное» время + смещение пояса tz на этот момент
  const asUtc = new Date(`${v}:00Z`);
  const off = tzOffsetMinutes(new Date(asUtc.getTime() - tzOffsetMinutes(asUtc, tz) * 60000), tz);
  const sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off);
  return `${v}:00${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}
function tzOffsetMinutes(d: Date, tz: string | null | undefined) {
  try {
    const s = new Intl.DateTimeFormat("en-US", { timeZone: tz ?? undefined, timeZoneName: "longOffset" }).format(d);
    const m = s.match(/GMT([+-])(\d{2}):?(\d{2})?/);
    if (!m) return 0;
    const v = Number(m[2]) * 60 + Number(m[3] ?? 0);
    return m[1] === "-" ? -v : v;
  } catch {
    return 0;
  }
}
